import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", birth_date: "", fiscal_code: "", address: "", emergency_contact: "", admin_notes: "", op_notes: "" };

export default function PatientModal({ open, onClose, onSaved, patient }) {
  const [form, setForm] = useState(patient ? { ...EMPTY, ...patient } : EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(patient?.id);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.put(`/patients/${patient.id}`, form)
        : await api.post("/patients", form);
      toast.success(isEdit ? "Paziente aggiornato" : "Paziente creato");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Errore nel salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="patient-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? "Modifica paziente" : "Nuovo paziente"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nome *</Label>
            <Input data-testid="patient-first-name" value={form.first_name} onChange={set("first_name")} className="rounded-xl" />
          </div>
          <div>
            <Label>Cognome *</Label>
            <Input data-testid="patient-last-name" value={form.last_name} onChange={set("last_name")} className="rounded-xl" />
          </div>
          <div>
            <Label>Telefono</Label>
            <Input data-testid="patient-phone" value={form.phone || ""} onChange={set("phone")} className="rounded-xl" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="patient-email" value={form.email || ""} onChange={set("email")} className="rounded-xl" />
          </div>
          <div>
            <Label>Data di nascita</Label>
            <Input data-testid="patient-birth-date" type="date" value={form.birth_date || ""} onChange={set("birth_date")} className="rounded-xl" />
          </div>
          <div>
            <Label>Codice fiscale</Label>
            <Input data-testid="patient-fiscal-code" value={form.fiscal_code || ""} onChange={set("fiscal_code")} className="rounded-xl" />
          </div>
          <div className="col-span-2">
            <Label>Indirizzo</Label>
            <Input data-testid="patient-address" value={form.address || ""} onChange={set("address")} className="rounded-xl" />
          </div>
          <div className="col-span-2">
            <Label>Contatto di emergenza</Label>
            <Input data-testid="patient-emergency" value={form.emergency_contact || ""} onChange={set("emergency_contact")} className="rounded-xl" />
          </div>
          <div className="col-span-2">
            <Label>Note amministrative</Label>
            <Textarea data-testid="patient-admin-notes" value={form.admin_notes || ""} onChange={set("admin_notes")} className="rounded-xl" rows={2} />
          </div>
          <div className="col-span-2">
            <Label>Note operative</Label>
            <Textarea data-testid="patient-op-notes" value={form.op_notes || ""} onChange={set("op_notes")} className="rounded-xl" rows={2} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" data-testid="patient-cancel-btn" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="patient-save-btn" onClick={save} disabled={saving} className="rounded-full">
            {saving ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea paziente"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
